/**
 * Gespeicherte Sprachpraeferenz — App-Haelfte des gemeinsamen Vertrags mit der
 * Website (`whocan-website/src/scripts/lang-pref.ts`). Beide Systeme liegen auf
 * derselben Origin (www.whocan.org) und lesen/schreiben deshalb dieselben
 * localStorage-Keys:
 *
 *   whocan.langPref       en|de|es|fr|it|pt — NUR bei expliziter Wahl geschrieben
 *                         (Mismatch-Banner, Sprachlink, Profil-Card). Nie implizit
 *                         aus der URL abgeleitet, sonst wuerde jeder geteilte Link
 *                         die Praeferenz ueberschreiben.
 *   whocan.langDismissed  "{urlLang}>{zielLang}" — letzte Banner-Ablehnung. Ein Wert,
 *                         wird ueberschrieben; ein anderes Sprachpaar darf den Banner
 *                         wieder zeigen.
 *
 * Dazu zwei Keys fuer den Chrome-≠-Inhalt-Hinweis (Konzept Prinzip 6):
 *
 *   whocan.justSwitched          sessionStorage — von JEDEM sprachwechselnden
 *                                Redirect (Globe, Banner, Hamburger, Profil —
 *                                App wie Website) direkt vor der Navigation
 *                                gesetzt. Die Ziel-Seite (nur App, nur vote*-
 *                                Routen) liest und loescht ihn.
 *   whocan.contentLangHintShown  localStorage — der Hinweis „Umfrage-Inhalte
 *                                bleiben in der Ersteller-Sprache" erscheint
 *                                genau einmal pro Browser.
 *
 * Bewusst framework-frei (kein Angular-Import), damit auch der frueheste
 * Init-Hook und Services ohne DI-Zyklus darauf zugreifen koennen.
 */

export const LANGUAGES = ['en', 'de', 'es', 'fr', 'it', 'pt'] as const;
export type LangCode = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: LangCode = 'en';
export const LANG_PREF_KEY = 'whocan.langPref';
export const LANG_DISMISSED_KEY = 'whocan.langDismissed';
export const JUST_SWITCHED_KEY = 'whocan.justSwitched';
export const CONTENT_LANG_HINT_SHOWN_KEY = 'whocan.contentLangHintShown';

export function isImplementedLanguage(value: string | null | undefined): value is LangCode {
    return !!value && (LANGUAGES as readonly string[]).includes(value);
}

/** localStorage kann werfen (Safari Private Mode, deaktivierte Cookies). */
function readItem(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function writeItem(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Ohne Speicher laeuft alles weiter, nur ohne Erinnerung.
    }
}

/** Explizit gewaehlte Sprache, oder null wenn keine (gueltige) gespeichert ist. */
export function getLangPref(): LangCode | null {
    const stored = readItem(LANG_PREF_KEY);
    return isImplementedLanguage(stored) ? stored : null;
}

/** Nur bei EXPLIZITER Wahl aufrufen. */
export function setLangPref(lang: string): void {
    if (!isImplementedLanguage(lang)) {
        return;
    }
    writeItem(LANG_PREF_KEY, lang);
}

/**
 * Uebernimmt eine Sprache (z. B. DbUser.language beim Login) NUR, wenn noch keine
 * Praeferenz gespeichert ist. Ueberschreibt also nie eine echte Wahl.
 */
export function adoptLangPref(candidate: string | null | undefined): void {
    if (getLangPref()) {
        return;
    }
    setLangPref(normalizeLanguage(candidate));
}

/** 'de-CH' / 'en-US' → 'de' / 'en'; alles Unbekannte → ''. */
export function normalizeLanguage(value: string | null | undefined): string {
    const code = value ? value.slice(0, 2).toLowerCase() : '';
    return isImplementedLanguage(code) ? code : '';
}

/** Browsersprache, falls sie zu einer der 6 implementierten Sprachen gehoert. */
export function getBrowserLang(): LangCode | null {
    if (typeof navigator === 'undefined') {
        return null;
    }
    const raw = (navigator.languages && navigator.languages[0]) || navigator.language;
    const code = normalizeLanguage(raw);
    return isImplementedLanguage(code) ? code : null;
}

/**
 * Praeferenz → Browsersprache (falls implementiert) → 'en'.
 * Grundlage fuer Banner-Vergleich und Aufloesung prefix-loser Einstiege.
 */
export function getEffectiveLang(): LangCode {
    return getLangPref() ?? getBrowserLang() ?? DEFAULT_LANGUAGE;
}

/** Merkt die Banner-ABLEHNUNG (nicht das Anzeigen) fuer genau dieses Sprachpaar. */
export function setLangDismissed(urlLang: string, targetLang: string): void {
    writeItem(LANG_DISMISSED_KEY, `${urlLang}>${targetLang}`);
}

export function isLangDismissed(urlLang: string, targetLang: string): boolean {
    return readItem(LANG_DISMISSED_KEY) === `${urlLang}>${targetLang}`;
}

/** Direkt vor jedem sprachwechselnden Redirect aufrufen (Globe, Banner, Hamburger, Profil). */
export function markLanguageSwitch(): void {
    try {
        window.sessionStorage.setItem(JUST_SWITCHED_KEY, '1');
    } catch (e) {
        // Ohne Speicher entfaellt nur der einmalige Inhalts-Hinweis.
    }
}

/** Liest UND loescht den Wechsel-Flag — nur von vote*-Routen aufzurufen. */
export function consumeLanguageSwitchFlag(): boolean {
    try {
        const wasSwitched = window.sessionStorage.getItem(JUST_SWITCHED_KEY) === '1';
        window.sessionStorage.removeItem(JUST_SWITCHED_KEY);
        return wasSwitched;
    } catch (e) {
        return false;
    }
}

export function wasContentLangHintShown(): boolean {
    return readItem(CONTENT_LANG_HINT_SHOWN_KEY) === '1';
}

export function markContentLangHintShown(): void {
    writeItem(CONTENT_LANG_HINT_SHOWN_KEY, '1');
}
